/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { usePageSpaceKey } from '@confluence/page-context';
import { getIsSiteAdminFromQuery } from '@confluence/current-user/entry-points/getIsSiteAdminFromQuery';
import { getIsProductOrSiteAdminFromQuery } from '@confluence/current-user/entry-points/getIsProductOrSiteAdminFromQuery';

import { PublicLinkPageStatus } from './__types__/usePublicLinkPageDataProviderPublicLinkPageQuery';
import type {
	useIsPLEmpoweredUserPublicLinkUserAdminStatusQuery as useIsPLEmpoweredUserPublicLinkUserAdminStatusQuery$data,
	useIsPLEmpoweredUserPublicLinkUserAdminStatusQueryVariables as useIsPLEmpoweredUserPublicLinkUserAdminStatusQuery$variables,
} from './__types__/useIsPLEmpoweredUserPublicLinkUserAdminStatusQuery';

enum AccessLevelRequired {
	space = 'space',
	org = 'org',
	product = 'product',
}

const publicLinkStatusToAccessLevelRequiredMappings = {
	[PublicLinkPageStatus.BLOCKED_BY_SPACE]: AccessLevelRequired.space,
	[PublicLinkPageStatus.SPACE_DISABLED]: AccessLevelRequired.space,
	[PublicLinkPageStatus.BLOCKED_BY_PRODUCT]: AccessLevelRequired.product,
	[PublicLinkPageStatus.SITE_DISABLED]: AccessLevelRequired.product,
	[PublicLinkPageStatus.SPACE_BLOCKED]: AccessLevelRequired.product,
	[PublicLinkPageStatus.BLOCKED_BY_ORG]: AccessLevelRequired.org,
	[PublicLinkPageStatus.SITE_BLOCKED]: AccessLevelRequired.org,
	[PublicLinkPageStatus.BLOCKED_BY_CONTAINER_POLICY]: AccessLevelRequired.org,
	[PublicLinkPageStatus.BLOCKED_BY_CLASSIFICATION_LEVEL]: AccessLevelRequired.org,
};

const getAccessLevelRequired = (publicLinkStatus: PublicLinkPageStatus) => {
	if (!publicLinkStatus) return null;
	return publicLinkStatusToAccessLevelRequiredMappings[publicLinkStatus] || null;
};

interface UseIsPLEmpoweredUserProps {
	publicLinkStatus: PublicLinkPageStatus | null;
	skip?: boolean;
}

/**
 * This only works from the context of the space where page with the public link is
 * */
export const useIsPLEmpoweredUser = ({ publicLinkStatus, skip }: UseIsPLEmpoweredUserProps) => {
	// get admin level of current user, and derive their access level
	// get access level needed to unblock public link
	const [spaceKey] = usePageSpaceKey();

	const { data, error } = useQuery<
		useIsPLEmpoweredUserPublicLinkUserAdminStatusQuery$data,
		useIsPLEmpoweredUserPublicLinkUserAdminStatusQuery$variables
	>(
		graphql`
			query useIsPLEmpoweredUserPublicLinkUserAdminStatusQuery($key: String) {
				user: confluence_atlassianUser(current: true) {
					id
					confluence {
						operations {
							operation
							targetType
						}
					}
				}
				isSiteAdmin
				hasUserAccessAdminRole
				space(key: $key) {
					currentUser {
						isAdmin
					}
				}
			}
		`,
		{
			variables: { key: spaceKey },
			skip: skip || !publicLinkStatus,
		},
	);

	if (skip || !publicLinkStatus) {
		return {
			isEmpoweredUser: false,
		};
	}

	const isSpaceAdmin = Boolean(data?.space?.currentUser?.isAdmin);
	const isSiteAdmin = getIsSiteAdminFromQuery(data);
	const isProductOrSiteAdmin = getIsProductOrSiteAdminFromQuery(data);

	const accessLevelRequired: AccessLevelRequired | null = getAccessLevelRequired(publicLinkStatus);

	const isEmpoweredUserForAccessLevel = (accessLevelRequired: AccessLevelRequired | null) => {
		// User is not blocked, is empowered automatically
		if (!accessLevelRequired) {
			return true;
		}
		// If user is site admin, they can make any needed changes
		if (accessLevelRequired === AccessLevelRequired.org) {
			return isSiteAdmin;
		}
		return (
			isProductOrSiteAdmin ||
			// Otherwise, they can only fix it if it's a space access issue and they
			// are space admin
			(accessLevelRequired === AccessLevelRequired.space && isSpaceAdmin)
		);
	};

	return {
		isEmpoweredUser: isEmpoweredUserForAccessLevel(accessLevelRequired),
		isSpaceAdmin,
		isSiteAdmin,
		error,
	};
};
